import * as React from "react";
import classNames from "classnames";
import ImageTextCtaSliderItem, {
    ImageTextCtaSliderItemProps
} from "./image-text-cta-slider-item/image-text-cta-slider-item";
import Slider, { Settings } from "react-slick";
import { ThemeColorType } from "~/types/theme-color";
import ContentSection from "../content-section/content-section";

export interface ImageTextCtaSliderProps {
    slides: ImageTextCtaSliderItemProps[];
    autoslide?: boolean;
    autoslideSpeed?: 4000 | 6000;
    fade?: boolean;
    bgColor?: ThemeColorType;
    textColor?: ThemeColorType;
    ctaBgColor?: ThemeColorType;
    ctaTextColor?: ThemeColorType;
    navigationColor?: ThemeColorType;
    anchorId?: string;
}

const ImageTextCtaSlider = ({
    slides,
    autoslide = false,
    autoslideSpeed = 4000,
    fade = false,
    bgColor,
    textColor,
    ctaBgColor,
    ctaTextColor,
    ...props
}: ImageTextCtaSliderProps) => {
    const [beforeChangeCurrentSlide, setBeforeChangeCurrentSlide] = React.useState<number | undefined>(0);
    const [afterChangeCurrentSlide, setAfterChangeCurrentSlide] = React.useState<number | undefined>();
    const [activeSlideIndex, setActiveSlideIndex] = React.useState(0);
    const wasActiveSlideIndex =
        activeSlideIndex === afterChangeCurrentSlide ? beforeChangeCurrentSlide : undefined;
    const navigationColor =
        slides[activeSlideIndex].navigationColor || props.navigationColor || ThemeColorType.BLACK;

    const baseClass = "image-text-cta-slider";
    const componentClasses = classNames(baseClass, {
        [`${baseClass}--auto-slide-speed-${autoslideSpeed}`]: autoslide && autoslideSpeed,
        [`${baseClass}--auto-slide`]: autoslide
    });
    const sliderSettings: Settings = {
        slidesToScroll: 1,
        slidesToShow: 1,
        // Infinite=true creates cloned slides and is only needed if autoplaying
        // So for performance we turn it off if no autoplay
        // NB: The cloned slides would create problems if we autoplay videos without muting!!!
        infinite: autoslide,
        autoplay: autoslide,
        autoplaySpeed: autoslideSpeed,
        speed: 700,
        focusOnSelect: false,
        fade: fade,
        // lazyLoad: "ondemand", // Lazy loading would be nice, but then we cannot keep equal height on all slides...🤷‍♀️
        pauseOnHover: true,
        arrows: false,
        dots: true,
        dotsClass: `${baseClass}__dots`,
        beforeChange: (current, next) => {
            // Inorder to first stop the video after the animation
            // we need to get the currentIndex before starting the animation
            setBeforeChangeCurrentSlide(current);
            setActiveSlideIndex(next);
        },
        afterChange: current => {
            // We cannot get the previous current index at this point,
            // but we can set this to figure out that the animation is over
            setAfterChangeCurrentSlide(current);
        },
        customPaging: i => (
            <button
                type="button"
                className={classNames(`${baseClass}__dot`, `theme-color-${navigationColor}`, "reset-button")}
            >
                <svg className="progress-ring" width="36" height="36">
                    <circle
                        className="progress-ring__circle"
                        strokeWidth="2"
                        fill="transparent"
                        r="16"
                        cx="18"
                        cy="18"
                    />
                </svg>
                {i + 1}
            </button>
        )
    };

    return (
        <ContentSection className={componentClasses} {...props}>
            <Slider {...sliderSettings}>
                {slides.map((slide, index) => (
                    <ImageTextCtaSliderItem
                        key={index}
                        {...slide}
                        bgColor={slide.bgColor || bgColor}
                        textColor={slide.textColor || textColor}
                        ctaBgColor={slide.ctaBgColor || ctaBgColor}
                        ctaTextColor={slide.ctaTextColor || ctaTextColor}
                        activeSlideIndex={activeSlideIndex}
                        wasActiveSlideIndex={wasActiveSlideIndex}
                        index={index}
                    />
                ))}
            </Slider>
        </ContentSection>
    );
};

export default ImageTextCtaSlider;
